import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label, Modal, TextField } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { useAttributeValueForm } from './useAttributeValueForm';

type AttributeValueFormModalViewProps = {
    error: string | null;
    form: ReturnType<typeof useAttributeValueForm>;
    isOpen: boolean;
    isSubmitting: boolean;
    mode: 'create' | 'edit';
    onClose: () => void;
};

export const AttributeValueFormModalView: React.FC<
    AttributeValueFormModalViewProps
> = ({ error, form, isOpen, isSubmitting, mode, onClose }) => {
    const { t } = useTranslation('pages');
    const { name, slug, isValid, onNameChange, onSlugChange, onSubmit } = form;

    const copy = React.useMemo(
        () => ({
            title:
                mode === 'edit'
                    ? t('attributes.components.dictionary.valueForm.editTitle')
                    : t('attributes.components.dictionary.valueForm.title'),
            nameLabel: t(
                'attributes.components.dictionary.valueForm.nameLabel',
            ),
            namePlaceholder: t(
                'attributes.components.dictionary.valueForm.namePlaceholder',
            ),
            slugLabel: t(
                'attributes.components.dictionary.valueForm.slugLabel',
            ),
            slugPlaceholder: t(
                'attributes.components.dictionary.valueForm.slugPlaceholder',
            ),
            cancel: t('attributes.components.dictionary.valueForm.cancel'),
            submit:
                mode === 'edit'
                    ? t('attributes.components.dictionary.valueForm.editSubmit')
                    : t('attributes.components.dictionary.valueForm.submit'),
        }),
        [mode, t],
    );

    return (
        <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
            <Modal.Backdrop>
                <Modal.Container size="sm">
                    <Modal.Dialog
                        {...getNodeTestIdProp('attribute-value-form')}
                    >
                        <Modal.CloseTrigger />
                        <Modal.Header
                            {...getNodeTestIdProp(
                                'attribute-value-form-header',
                            )}
                        >
                            <Modal.Heading
                                {...getNodeTestIdProp(
                                    'attribute-value-form-title',
                                )}
                            >
                                {copy.title}
                            </Modal.Heading>
                        </Modal.Header>
                        <Modal.Body
                            className="flex flex-col gap-5"
                            {...getNodeTestIdProp('attribute-value-form-body')}
                        >
                            <TextField
                                isRequired
                                onChange={onNameChange}
                                value={name}
                                {...getNodeTestIdProp(
                                    'attribute-value-form-name',
                                )}
                            >
                                <Label>{copy.nameLabel}</Label>
                                <Input placeholder={copy.namePlaceholder} />
                            </TextField>
                            <TextField
                                isRequired
                                onChange={onSlugChange}
                                value={slug}
                                {...getNodeTestIdProp(
                                    'attribute-value-form-slug',
                                )}
                            >
                                <Label>{copy.slugLabel}</Label>
                                <Input placeholder={copy.slugPlaceholder} />
                            </TextField>
                            {error ? (
                                <p
                                    className="text-sm text-danger"
                                    {...getNodeTestIdProp(
                                        'attribute-value-form-error',
                                    )}
                                >
                                    {error}
                                </p>
                            ) : null}
                        </Modal.Body>
                        <Modal.Footer
                            {...getNodeTestIdProp(
                                'attribute-value-form-footer',
                            )}
                        >
                            <Button
                                isDisabled={isSubmitting}
                                onPress={onClose}
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    'attribute-value-form-cancel',
                                )}
                            >
                                {copy.cancel}
                            </Button>
                            <Button
                                isDisabled={!isValid}
                                isPending={isSubmitting}
                                onPress={onSubmit}
                                variant="primary"
                                {...getNodeTestIdProp(
                                    'attribute-value-form-submit',
                                )}
                            >
                                {copy.submit}
                            </Button>
                        </Modal.Footer>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};
